import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SaveButtonWithModal from './SaveButtonWithModel';

const MembershipSection = ({
  selectedMemberships = [],
  membershipData = {},
  setSelectedMemberships,
  customInputBoxes = [],
  setCustomInputBoxes,
  onSave
}) => {
  const secondContentRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [customInput, setCustomInput] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [inputs, setInputs] = useState(membershipData.inputs || {});
  // Scrollbar state
  const [scrollTop, setScrollTop] = useState(0);
  const [thumbHeight, setThumbHeight] = useState(20);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startScrollTop, setStartScrollTop] = useState(0);
  const thumbRef = useRef(null);
  const inputRef = useRef(null);

  const handleRemoveMembership = (membershipToRemove) => {
    const updatedMemberships = selectedMemberships.filter(
      membership => membership !== membershipToRemove
    );
    setSelectedMemberships(updatedMemberships);
    
    // Remove all custom input boxes associated with this membership
    const membershipIndex = selectedMemberships.indexOf(membershipToRemove);
    const updatedBoxes = customInputBoxes.filter(
      box => box.membershipId !== membershipIndex
    );
    setCustomInputBoxes(updatedBoxes);
    
    if (onSave) {
      onSave({ 
        selected: updatedMemberships,
        customInputBoxes: updatedBoxes
      });
    }
  };
  const handleButtonScroll = (direction, contentRef) => {
    if (contentRef.current) {
      contentRef.current.scrollBy({
        top: direction === 'up' ? -50 : 50,
        behavior: 'smooth'
      });
    }
  };

  const handleCustomInputChange = (index, value) => {
    const updatedBoxes = [...customInputBoxes];
    updatedBoxes[index].value = value;
    setCustomInputBoxes(updatedBoxes);
  };

  const handleRemoveCustomInput = (index) => {
    const updatedBoxes = [...customInputBoxes];
    updatedBoxes.splice(index, 1);
    setCustomInputBoxes(updatedBoxes);
  };

  const handlePlusClick = (membershipIndex) => {
    // Only add additional inputs (not main ones)
    setCustomInputBoxes([
      ...customInputBoxes,
      { 
        membershipId: membershipIndex, 
        value: '',
        id: Date.now(),
        isMainInput: false
      }
    ]);
  };

  const membershipOptions = [
    "ASSOCIATION (ASS)", "ALLIANCE (ALL)", "CHAMA (CHA)", "CONTRACT (CON)", "COMMUNITY (COM)", "COOPERATIVE (COP)", "GROUP (GRO)", "MEMBERSHIP (MEM)", "SAVINGS AND CREDIT (SAC)", "STOKVEL (STO)", "UNION (UNI)"

  ];
  const [organizationName, setOrganizationName] = useState(membershipData.organizationName || '');
  const [membershipNumber, setMembershipNumber] = useState(membershipData.membershipNumber || '');
  const [membershipType, setMembershipType] = useState(membershipData.membershipType || '');
  const [membershipDate, setMembershipDate] = useState(membershipData.membershipDate || '');
  
  const handleSave = () => {
    const data = {
      selected: selectedMemberships,
      custom: [], // This can be populated if you have custom text entries
      customInputBoxes,
      inputs,
      organizationName,
      membershipNumber,
      membershipType,
      membershipDate
    };
    
    onSave(data);
  };
  const handleInputChange = (key, value) => {
    setInputs(prev => ({
      ...prev,
      [key]: value
    }));
  };
  
  const handleSelect = (option) => {
    if (option === "Other (please specify)") {
      setShowCustomInput(true);
    } else {
      const updated = selectedMemberships.includes(option)
        ? selectedMemberships.filter(m => m !== option)
        : [...selectedMemberships, option];
      
      setSelectedMemberships(updated);
      
      // Initialize main input for newly added memberships
      if (!selectedMemberships.includes(option)) {
        const membershipIndex = updated.indexOf(option);
        const hasMainInput = customInputBoxes.some(
          box => box.membershipId === membershipIndex && box.isMainInput
        );
        
        if (!hasMainInput) {
          setCustomInputBoxes(prev => [
            ...prev,
            {
              membershipId: membershipIndex,
              value: '',
              id: Date.now(),
              isMainInput: true
            }
          ]);
        }
      }
      
      // Close the dropdown after selection
      setIsDropdownOpen(false);
      
      if (onSave) onSave({ 
        selected: updated, 
        customInputBoxes: customInputBoxes.filter(box => 
          updated.some((_, idx) => idx === box.membershipId)
        ),
        inputs
      });
    }
  };

  const handleCustomSave = () => {
    if (customInput.trim()) {
      const updated = [...selectedMemberships, customInput];
      setSelectedMemberships(updated);
      setShowCustomInput(false);
      setCustomInput("");
      if (onSave) onSave({ 
        selected: updated, 
        custom: customInputBoxes,
        customInputBoxes,
        inputs
      });
    }
  };

  const handleAddCustomMembership = (value, membershipIndex, inputIndex) => {
    if (value.trim() && !selectedMemberships.includes(value)) {
      const updatedMemberships = [...selectedMemberships, value];
      setSelectedMemberships(updatedMemberships);
      
      const updatedBoxes = [...customInputBoxes];
      updatedBoxes.splice(inputIndex, 1);
      setCustomInputBoxes(updatedBoxes);
      
      if (onSave) {
        onSave({ 
          selected: updatedMemberships,
          custom: updatedBoxes,
          customInputBoxes: updatedBoxes,
          inputs
        });
      }
    }
  };

  useEffect(() => {
    const content = secondContentRef.current;
    if (!content) return;
  
    const updateThumbHeight = () => {
      const { scrollHeight, clientHeight } = content;
      const visibleRatio = clientHeight / scrollHeight;
      setThumbHeight(Math.max(visibleRatio * (clientHeight - 40), 20));
    };
  
    updateThumbHeight();
    window.addEventListener("resize", updateThumbHeight);
    return () => window.removeEventListener("resize", updateThumbHeight);
  }, [isDropdownOpen]);
  
  const handleScroll = () => {
    const content = secondContentRef.current;
    if (!content) return;
  
    const { scrollTop, scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    const scrollRatio = maxScrollTop > 0 ? scrollTop / maxScrollTop : 0;
    
    const trackHeight = content.clientHeight - 40;
    const thumbOffset = scrollRatio * (trackHeight - thumbHeight);
    setScrollTop(thumbOffset);
  };

  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);
    setStartY(e.clientY);
    setStartScrollTop(secondContentRef.current.scrollTop);

    document.addEventListener("mousemove", handleThumbMouseMove);
    document.addEventListener("mouseup", handleThumbMouseUp);
    document.body.style.cursor = "grabbing";
  };

  const handleThumbMouseMove = (e) => {
    if (!isDragging) return;

    const deltaY = e.clientY - startY;
    const content = secondContentRef.current;
    const { scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    const trackHeight = clientHeight - thumbHeight;

    const scrollDelta = (deltaY / trackHeight) * maxScrollTop;
    const newScrollTop = Math.min(
      Math.max(0, startScrollTop + scrollDelta),
      maxScrollTop
    );

    content.scrollTop = newScrollTop;
  };

  const handleThumbMouseUp = () => {
    setIsDragging(false);
    document.removeEventListener("mousemove", handleThumbMouseMove);
    document.removeEventListener("mouseup", handleThumbMouseUp);
    document.body.style.cursor = "";
  };

  const handleTrackClick = (e) => {
    const content = secondContentRef.current;
    if (!content || e.target === thumbRef.current) return;

    const track = e.currentTarget;
    const trackRect = track.getBoundingClientRect();
    const clickPosition = e.clientY - trackRect.top;
    const thumbCenter = thumbHeight / 2;
    const scrollRatio = (clickPosition - thumbCenter) / (trackRect.height - thumbHeight);

    const { scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    content.scrollTop = scrollRatio * maxScrollTop;
  };

  useEffect(() => {
    const handleInputFocus = () => {
      setIsDropdownOpen(true);
    };

    const inputElement = inputRef.current;
    if (inputElement) {
      inputElement.addEventListener('focus', handleInputFocus);
      return () => {
        inputElement.removeEventListener('focus', handleInputFocus);
      };
    }
  }, []);

  const UnselectedIcon = () => (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle 
        cx="8.5" 
        cy="8.5" 
        r="8" 
        fill="#D9D9D9" 
        stroke="white" 
        strokeWidth="2"
      />
    </svg>
  );

  const SelectedIcon = () => (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8.5" cy="8.5" r="8" fill="#2E453D" stroke="#FFBD26"/>
      <path d="M5 8.5L7.5 11L12 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  return (
    <div className="space-y-4 mt-5">
      <p className="md:text-[24px] text-[8px] italic font-medium text-center md:mt-4 mt-2 md:mb-4">
        Please indicate any other organizations, groups, or affiliations you are currently a member of or actively involved with.
      </p>
      
      <div className="flex flex-col space-y-2">
        <div className="relative">
          <button className="relative md:h-[71px] h-[40px] w-full flex items-center justify-between px-4 rounded-[5px] cursor-pointer bg-[#152C24] text-white">
            <span className="text-[8px] md:text-[32px] font-medium truncate">
              {selectedMemberships.length > 0 ? selectedMemberships[0] : 'XXXXXXXXX'}
            </span>
            <button onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
              <div className="absolute top-0 right-0 w-[50px] h-[40px] md:h-full md:w-[150px] bg-[#D9D9D9] text-white flex justify-center items-center rounded-r-[5px] cursor-pointer">
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1748785688/Expand_down_yilqh4.png"
                  alt="expand"
                  className={`w-2 h-2 md:w-9 md:h-[35px] transition-transform duration-300 ${
                    isDropdownOpen ? 'rotate-180' : ''
                  }`}
                />
                
              </div>
            </button>
          </button>
          <p className="text-right text-[4px] md:text-xs font-medium italic mt-1">Select category</p>
        </div>
        
        <AnimatePresence>
          {isDropdownOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="relative z-50"
            >
              <div className="md:w-full bg-[#CFD7D7] rounded-[5px] shadow-lg p-2 relative">
                <div 
                  ref={secondContentRef}
                  onScroll={handleScroll}
                  className="max-h-[300px] overflow-y-auto pr-4 scrollbar-hidden relative"
                >
                  <div 
                    className="absolute top-2 md:left-[380px] z-10 cursor-pointer"
                    onClick={() => setIsDropdownOpen(false)}  
                  >
                    <img
                      src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1752766949/Export_h76rtd.png"
                      alt="check"
                      className="w-4 h-4 md:w-6 md:h-6"
                    />
                  </div>

                  <div className="md:mt-10 mt-2">
                    {membershipOptions.map((option, index) => (
                      <div
                        key={index}
                        className={`p-2 cursor-pointer flex items-center ${
                          selectedMemberships.includes(option) ? 'bg-transparent' : ''
                        }`}
                        onClick={() => handleSelect(option)}
                      >
                        <div className="flex items-center   justify-between md:w-[400px] w-full">
                          <span className="text-[8px] md:text-[16px] uppercase">
                            {option.toUpperCase()}
                          </span>
                          <div className="mr-2">
                            {selectedMemberships.includes(option) ? (
                              <SelectedIcon />
                            ) : (
                              <UnselectedIcon />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="absolute right-2 top-2 bottom-2 md:w-[25px] w-2 bg-gray-200 rounded-[5px] flex flex-col items-center">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleButtonScroll('up', secondContentRef);
                    }}
                    className="w-full flex justify-center py-1 md:py-2 h-[20px]"
                  >
                    <img 
                      src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1752766873/Expand_up_light_xir9b2.png" 
                      alt="Scroll up" 
                      className="md:w-3 md:h-3 w-2 h-2"
                    />
                  </button>

                  <div 
                    className="flex-1 w-full relative min-h-[20px]"
                    onClick={handleTrackClick}
                  >
                    <div
                      ref={thumbRef}
                      className="w-full bg-gray-500 md:w-[15px] md:ml-[5px] rounded-[10px] cursor-grab absolute"
                      style={{
                        height: `${thumbHeight}px`,
                        top: `${scrollTop}px`,
                      }}
                      onMouseDown={handleThumbMouseDown}
                    />
                  </div>

                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleButtonScroll('down', secondContentRef);
                    }}
                    className="w-full flex justify-center py-1 md:py-2 h-[20px]"
                  >
                    <img 
                      src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1752766872/Expand_down_light_oppxas.png" 
                      alt="Scroll down" 
                      className="md:w-3 md:h-3 w-2 h-2"
                    />
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="space-y-2">
        {selectedMemberships.map((membership, index) => (
          <div key={`membership-${index}`}>
            <div className="flex md:mt-3 mt-1 w-full h-[30px] md:h-[80px]  bg-[#F5F5F5] rounded-[5px]">
              <div className="md:w-[600px] w-[100px] bg-[#D9D9D9] flex items-center justify-between px-1 md:px-6">
                <span className="font-medium text-[6px] md:text-[32px] leading-tight">
                  {membership.split(' ')[0].toUpperCase()}
                </span>
                <div className="flex flex-col items-center ">
                  <button 
                    className="font-medium text-[6px] md:text-[32px] leading-tight cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePlusClick(index);
                    }}
                  >
                    +
                  </button>
                </div>
              </div>
              <div className="w-full flex items-center justify-center pr-2 relative">
                <img
                  src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1751453134/Import_1_kwyipy.png"
                  alt="hi"
                  className="absolute top-0 left-4 md:left-5 md:top-1 md:w-[24px] md:h-[24px] w-[10px] h-[10px]"
                />
            <input
  ref={inputRef}
  type="text"
  placeholder="XXXXXX"
  value={
    customInputBoxes.find(box => 
      box.membershipId === index && box.isMainInput
    )?.value || ''
  }
  onChange={(e) => {
    const existingIndex = customInputBoxes.findIndex(
      box => box.membershipId === index && box.isMainInput
    );
    
    if (existingIndex >= 0) {
      // Update existing main input
      const updatedBoxes = [...customInputBoxes];
      updatedBoxes[existingIndex].value = e.target.value;
      setCustomInputBoxes(updatedBoxes);
    } else {
      // Create new main input if it doesn't exist
      setCustomInputBoxes([
        ...customInputBoxes,
        {
          membershipId: index,
          value: e.target.value,
          id: Date.now(),
          isMainInput: true
        }
      ]);
    }
  }}
  className="bg-transparent md:ml-8 ml-5 placeholder-black text-black text-left h-[30px] md:h-[80px] font-medium border-[#F5F5F5] w-full text-[6px] md:text-[28px] px-6 md:px-4 outline-none"
/>
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1750884947/Trash_l5buzt.png"
                  alt="Trash Icon"
                  className="absolute right-2 md:right-4 w-3 md:w-6 h-3 md:h-6 cursor-pointer"
                  onClick={() => handleRemoveMembership(membership)}
                />
                <span className="absolute bottom-2 left-5 text-[4px] md:text-xs font-medium italic">Type Name</span>
              </div>
            </div>

            {customInputBoxes
              .filter(box => box.membershipId === index && !box.isMainInput)
              .map((box) => (
                <div 
                  key={`custom-${box.id}`}
                  className="flex md:mt-2 mt-1 w-full h-[30px] md:h-[80px] rounded-[10px]"
                >
                  <div className="md:w-[600px] flex items-center justify-between px-1 md:px-6">
                  </div>

                  <div className="w-full flex items-center rounded-[10px] justify-center pr-2 relative bg-[#F5F5F5]">
                    <img
                      src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1751453134/Import_1_kwyipy.png"
                      alt="hi"
                      className="absolute top-0 left-4 md:left-5 md:top-1 md:w-[24px] md:h-[24px] w-[10px] h-[10px]"
                    />
                <input
                      type="text"
                      value={box.value}
                      onChange={(e) => {
                        const updatedBoxes = customInputBoxes.map(b => 
                          b.id === box.id ? {...b, value: e.target.value} : b
                        );
                        setCustomInputBoxes(updatedBoxes);
                      }}
                      placeholder="XXXXX"
                      className="bg-transparent md:ml-8 ml-5 text-black text-left h-[30px] md:h-[80px] placeholder:text-black font-medium border-[#F5F5F5] w-full text-[8px] md:text-[28px] focus:outline-none pr-6 md:pr-12"
                    />
                    
                    
                    <img
                      src="https://res.cloudinary.com/dzngpgki4/image/upload/v1750884947/Trash_l5buzt.png"
                      alt="Delete Icon"
                      className="absolute right-2 md:right-4 w-3 md:w-6 h-3 md:h-6 cursor-pointer"
                      onClick={() => {
                        setCustomInputBoxes(customInputBoxes.filter(b => b.id !== box.id));
                      }}
                    />
                    
                    <span className="absolute bottom-1 md:bottom-2 left-5 text-[4px] md:text-xs font-medium italic">Type Name</span>
                  </div>
                </div>
              ))}
          </div>
        ))}
      </div>

      <style jsx>{`
        .custom-scroll-area {
          position: relative;
          max-height: 300px;
          display: flex;
          overflow: hidden;
          width: 100%;
        }

        .custom-scroll-content {
          flex: 1;
          overflow-y: scroll;
          scrollbar-width: none;
          -ms-overflow-style: none;
          padding-right: 4px;
        }

        .custom-scroll-content::-webkit-scrollbar {
          display: none;
        }

        .custom-scroll-track {
          width: 8px;
          background: #F5F5F5;
          border-radius: 3px;
          position: relative;
          margin-left: 4px;
          cursor: pointer;
        }

        .custom-scroll-thumb {
          width: 100%;
          background: #979797;
          border-radius: 10px;
          position: absolute;
          right: 0;
          transition: background 0.2s;
        }

        .custom-scroll-thumb:hover {
          background: #444;
        }

        @media (max-width: 768px) {
          .custom-scroll-track {
            width: 4px;
            margin-left: 2px;
          }
          .custom-scroll-area {
            position: relative;
            max-height: 150px;
            display: flex;
            overflow: hidden;
            width: 100%;
          }
        }
      `}</style>
      <SaveButtonWithModal/>
    </div>
  );
};

export default MembershipSection;